import React, { useState } from 'react';
import { Copy, Download, Check } from 'lucide-react';
import { useToast } from '../../ToastNotifications';

export const GradientDemo = ({ colors }) => {
    const [copied, setCopied] = useState("");
    const { showCopyToast, showDownloadToast } = useToast();

    const copyToClipboard = async (text, type, name) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(`${name}-${type}`);
            showCopyToast(name, type);
            setTimeout(() => setCopied(""), 2000);
        } catch (err) {
            console.error("Failed to copy: ", err);
        }
    };

    const downloadFile = (content, filename, contentType) => {
        const blob = new Blob([content], { type: contentType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        const format = filename.split('.').pop();
        showDownloadToast(filename.replace(`.${format}`, ''), format);
    };

    // Convert CSS gradient to SVG
    const cssToSvg = (css, name, width = 400, height = 200) => {
        const gradientId = `gradient-${name.replace(/\s+/g, '-').toLowerCase()}`;
        let svgGradient = '';

        if (css.includes('linear-gradient')) {
            const match = css.match(/linear-gradient\(([^)]+)\)/);
            if (match) {
                const params = match[1].split(',').map(s => s.trim());
                let angle = '0';
                let colorStops = params;

                if (params[0].includes('deg') || params[0].includes('to ')) {
                    angle = params[0];
                    colorStops = params.slice(1);
                }

                let x1 = '0%', y1 = '0%', x2 = '100%', y2 = '0%';

                if (angle.includes('45deg') || angle === 'to top right') {
                    x1 = '0%'; y1 = '100%'; x2 = '100%'; y2 = '0%';
                } else if (angle.includes('90deg') || angle === 'to top') {
                    x1 = '0%'; y1 = '100%'; x2 = '0%'; y2 = '0%';
                } else if (angle.includes('135deg') || angle === 'to top left') {
                    x1 = '100%'; y1 = '100%'; x2 = '0%'; y2 = '0%';
                } else if (angle.includes('180deg') || angle === 'to left') {
                    x1 = '100%'; y1 = '0%'; x2 = '0%'; y2 = '0%';
                } else if (angle === 'to bottom') {
                    x1 = '0%'; y1 = '0%'; x2 = '0%'; y2 = '100%';
                } else if (angle === 'to right') {
                    x1 = '0%'; y1 = '0%'; x2 = '100%'; y2 = '0%';
                }

                svgGradient = `
    <defs>
        <linearGradient id="${gradientId}" x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}">
            ${colorStops.map((color, index) => {
                    const offset = colorStops.length > 1 ? (index / (colorStops.length - 1) * 100) : 0;
                    return `<stop offset="${offset}%" stop-color="${color.trim()}" />`;
                }).join('\n            ')}
        </linearGradient>
    </defs>`;
            }
        } else if (css.includes('radial-gradient')) {
            const match = css.match(/radial-gradient\(([^)]+)\)/);
            if (match) {
                const params = match[1].split(',').map(s => s.trim());
                let center = { cx: '50%', cy: '50%' };
                let colorStops = params;

                if (params[0].includes('at ')) {
                    const posMatch = params[0].match(/at\s+(.+)/);
                    if (posMatch) {
                        const pos = posMatch[1].trim();
                        if (pos.includes('top left')) { center = { cx: '0%', cy: '0%' }; }
                        else if (pos.includes('top right')) { center = { cx: '100%', cy: '0%' }; }
                        else if (pos.includes('bottom left')) { center = { cx: '0%', cy: '100%' }; }
                        else if (pos.includes('bottom right')) { center = { cx: '100%', cy: '100%' }; }
                    }
                    colorStops = params.slice(1);
                }

                svgGradient = `
    <defs>
        <radialGradient id="${gradientId}" cx="${center.cx}" cy="${center.cy}" r="50%">
            ${colorStops.map((color, index) => {
                    const offset = colorStops.length > 1 ? (index / (colorStops.length - 1) * 100) : 0;
                    return `<stop offset="${offset}%" stop-color="${color.trim()}" />`;
                }).join('\n            ')}
        </radialGradient>
    </defs>`;
            }
        } else if (css.includes('conic-gradient')) {
            // Approximate conic as radial gradient
            const match = css.match(/conic-gradient\(([^)]+)\)/);
            if (match) {
                const params = match[1].split(',').map(s => s.trim());
                let colorStops = params;

                if (params[0].includes('from ')) {
                    colorStops = params.slice(1);
                }

                svgGradient = `
    <defs>
        <radialGradient id="${gradientId}" cx="50%" cy="50%" r="50%">
            ${colorStops.map((color, index) => {
                    const offset = colorStops.length > 1 ? (index / (colorStops.length - 1) * 100) : 0;
                    return `<stop offset="${offset}%" stop-color="${color.trim()}" />`;
                }).join('\n            ')}
        </radialGradient>
    </defs>`;
            }
        }

        return `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
    ${svgGradient}
    <rect width="100%" height="100%" fill="url(#${gradientId})" />
    <text x="10" y="25" font-family="Arial, sans-serif" font-size="14" fill="white" fill-opacity="0.8">${name}</text>
</svg>`;
    };

    // Generate gradient combinations (your existing code)
    const getColor = (index) => colors[index]?.value || colors[0]?.value || '#3b82f6';

    const linearGradients = [
        { name: 'Primary to Secondary', css: `linear-gradient(45deg, ${getColor(0)}, ${getColor(1)})` },
        { name: 'Secondary to Accent', css: `linear-gradient(135deg, ${getColor(1)}, ${getColor(2)})` },
        { name: 'Accent to Support', css: `linear-gradient(90deg, ${getColor(2)}, ${getColor(3)})` },
        { name: 'Support to Primary', css: `linear-gradient(180deg, ${getColor(3)}, ${getColor(0)})` },
        { name: 'Primary Spectrum', css: `linear-gradient(45deg, ${getColor(0)}, ${getColor(1)}, ${getColor(2)})` },
        { name: 'Cool to Warm', css: `linear-gradient(90deg, ${getColor(2)}, ${getColor(1)}, ${getColor(0)})` },
        { name: 'Full Spectrum', css: `linear-gradient(135deg, ${getColor(0)}, ${getColor(1)}, ${getColor(2)}, ${getColor(3)})` },
        { name: 'Vertical Flow', css: `linear-gradient(to bottom, ${getColor(0)}, ${getColor(1)})` },
        { name: 'Horizontal Flow', css: `linear-gradient(to right, ${getColor(0)}, ${getColor(2)})` },
        { name: 'Diagonal Up', css: `linear-gradient(to top right, ${getColor(1)}, ${getColor(3)})` },
        { name: 'Diagonal Down', css: `linear-gradient(to bottom right, ${getColor(0)}, ${getColor(2)})` },
    ];

    const radialGradients = [
        { name: 'Center Burst', css: `radial-gradient(circle, ${getColor(0)}, ${getColor(1)})` },
        { name: 'Ellipse Fade', css: `radial-gradient(ellipse, ${getColor(1)}, ${getColor(2)})` },
        { name: 'Multi-Ring', css: `radial-gradient(circle, ${getColor(0)} 20%, ${getColor(1)} 40%, ${getColor(2)} 80%)` },
        { name: 'Corner Spotlight', css: `radial-gradient(circle at top left, ${getColor(0)}, ${getColor(3)})` },
        { name: 'Bottom Right', css: `radial-gradient(circle at bottom right, ${getColor(2)}, ${getColor(0)})` },
    ];

    const conicGradients = [
        { name: 'Color Wheel', css: `conic-gradient(${getColor(0)}, ${getColor(1)}, ${getColor(2)}, ${getColor(3)}, ${getColor(0)})` },
        { name: 'Half Rotation', css: `conic-gradient(from 90deg, ${getColor(0)}, ${getColor(1)})` },
        { name: 'Quarter Sections', css: `conic-gradient(${getColor(0)} 90deg, ${getColor(1)} 90deg 180deg, ${getColor(2)} 180deg 270deg, ${getColor(3)} 270deg)` },
        { name: 'Smooth Spin', css: `conic-gradient(from 45deg, ${getColor(0)}, ${getColor(2)}, ${getColor(1)}, ${getColor(0)})` },
    ];

    const complexGradients = [
        {
            name: 'Layered Blend',
            css: `linear-gradient(45deg, ${getColor(0)}80, ${getColor(1)}80), linear-gradient(135deg, ${getColor(2)}60, ${getColor(3)}60)`
        },
        {
            name: 'Mesh Pattern',
            css: `radial-gradient(circle at 20% 20%, ${getColor(0)}40, transparent 50%), radial-gradient(circle at 80% 80%, ${getColor(1)}40, transparent 50%), linear-gradient(45deg, ${getColor(2)}20, ${getColor(3)}20)`
        },
        {
            name: 'Glass Effect',
            css: `linear-gradient(135deg, ${getColor(0)}20, ${getColor(1)}40, ${getColor(0)}20)`
        },
    ];

    const GradientCard = ({ gradient, type }) => {
        const svgCode = cssToSvg(gradient.css, gradient.name);

        return (
            <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700">
                <div
                    className="h-32 relative group"
                    style={{ background: gradient.css }}
                >
                    {/* Hover overlay with action buttons */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg p-2 shadow-lg">
                            <div className="flex gap-2">
                                <button
                                    onClick={() => copyToClipboard(gradient.css, 'css', gradient.name)}
                                    className={`px-2 py-1 text-xs rounded transition-all ${copied === `${gradient.name}-css`
                                            ? "bg-green-500 text-white"
                                            : "bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                                        }`}
                                    title="Copy CSS"
                                >
                                    {copied === `${gradient.name}-css` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                </button>

                                <button
                                    onClick={() => copyToClipboard(svgCode, 'svg', gradient.name)}
                                    className={`px-2 py-1 text-xs rounded transition-all ${copied === `${gradient.name}-svg`
                                            ? "bg-green-500 text-white"
                                            : "bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-700 dark:text-blue-300"
                                        }`}
                                    title="Copy SVG"
                                >
                                    {copied === `${gradient.name}-svg` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                </button>

                                <button
                                    onClick={() => downloadFile(svgCode, `${gradient.name.toLowerCase().replace(/\s+/g, '-')}.svg`, 'image/svg+xml')}
                                    className="px-2 py-1 text-xs bg-purple-100 hover:bg-purple-200 dark:bg-purple-900 dark:hover:bg-purple-800 text-purple-700 dark:text-purple-300 rounded transition-all"
                                    title="Download SVG"
                                >
                                    <Download className="w-3 h-3" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-4">
                    <h6 className="font-semibold text-gray-900 dark:text-white mb-2">{gradient.name}</h6>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-mono bg-gray-50 dark:bg-gray-700 p-2 rounded mb-3">
                        {type}: {gradient.css.length > 60 ? gradient.css.substring(0, 60) + '...' : gradient.css}
                    </p>

                    {/* Action buttons */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => copyToClipboard(gradient.css, 'css', gradient.name)}
                            className={`flex-1 flex items-center justify-center px-3 py-2 text-xs rounded transition-all ${copied === `${gradient.name}-css`
                                    ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                                    : "bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                                }`}
                        >
                            {copied === `${gradient.name}-css` ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                            {copied === `${gradient.name}-css` ? "Copied!" : "Copy CSS"}
                        </button>

                        <button
                            onClick={() => copyToClipboard(svgCode, 'svg', gradient.name)}
                            className={`flex-1 flex items-center justify-center px-3 py-2 text-xs rounded transition-all ${copied === `${gradient.name}-svg`
                                    ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                                    : "bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-700 dark:text-blue-300"
                                }`}
                        >
                            {copied === `${gradient.name}-svg` ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                            {copied === `${gradient.name}-svg` ? "Copied!" : "Copy SVG"}
                        </button>

                        <button
                            onClick={() => downloadFile(svgCode, `${gradient.name.toLowerCase().replace(/\s+/g, '-')}.svg`, 'image/svg+xml')}
                            className="px-3 py-2 text-xs bg-purple-100 hover:bg-purple-200 dark:bg-purple-900 dark:hover:bg-purple-800 text-purple-700 dark:text-purple-300 rounded transition-all"
                            title="Download SVG"
                        >
                            <Download className="w-3 h-3" />
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-8">
            <div className="text-center">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Gradient Combinations</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    Beautiful gradients generated from your color palette. Copy CSS, copy SVG, or download as SVG files.
                </p>
            </div>

            {/* Linear Gradients */}
            <div>
                <h5 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <div className="w-4 h-4 mr-2 rounded" style={{ background: 'linear-gradient(45deg, #3b82f6, #8b5cf6)' }}></div>
                    Linear Gradients
                </h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {linearGradients.map((gradient, index) => (
                        <GradientCard key={index} gradient={gradient} type="linear-gradient" />
                    ))}
                </div>
            </div>

            {/* Radial Gradients */}
            <div>
                <h5 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <div className="w-4 h-4 mr-2 rounded-full" style={{ background: 'radial-gradient(circle, #10b981, #3b82f6)' }}></div>
                    Radial Gradients
                </h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {radialGradients.map((gradient, index) => (
                        <GradientCard key={index} gradient={gradient} type="radial-gradient" />
                    ))}
                </div>
            </div>

            {/* Conic Gradients */}
            <div>
                <h5 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <div className="w-4 h-4 mr-2 rounded-full" style={{ background: 'conic-gradient(#ef4444, #f59e0b, #10b981, #3b82f6, #ef4444)' }}></div>
                    Conic Gradients
                </h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {conicGradients.map((gradient, index) => (
                        <GradientCard key={index} gradient={gradient} type="conic-gradient" />
                    ))}
                </div>
            </div>

            {/* Complex Gradients */}
            <div>
                <h5 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <div className="w-4 h-4 mr-2 rounded" style={{ background: 'linear-gradient(45deg, #3b82f680, #8b5cf680), linear-gradient(135deg, #ef444460, #f59e0b60)' }}></div>
                    Complex Effects
                </h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {complexGradients.map((gradient, index) => (
                        <GradientCard key={index} gradient={gradient} type="complex" />
                    ))}
                </div>
            </div>

            {/* Gradient Tools */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
                <h5 className="text-base font-semibold text-blue-900 dark:text-blue-100 mb-3">
                    Enhanced Gradient Tools
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-blue-800 dark:text-blue-200">
                    <div>
                        <h6 className="font-semibold mb-2">🎨 CSS Gradients</h6>
                        <ul className="space-y-1">
                            <li>• Perfect for web backgrounds</li>
                            <li>• Great for button effects</li>
                            <li>• Ideal for hover states</li>
                        </ul>
                    </div>
                    <div>
                        <h6 className="font-semibold mb-2">📐 SVG Gradients</h6>
                        <ul className="space-y-1">
                            <li>• Scalable vector graphics</li>
                            <li>• Works in design tools</li>
                            <li>• Perfect for print</li>
                        </ul>
                    </div>
                    <div>
                        <h6 className="font-semibold mb-2">💡 Usage Tips</h6>
                        <ul className="space-y-1">
                            <li>• Test contrast for text</li>
                            <li>• Use transparency for layering</li>
                            <li>• Combine gradients for depth</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};